package com.hyperether.goodjob.scenes.scheduler

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.material.Divider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.navigation.NavHostController
import com.hyperether.goodjob.boxCornerShape
import com.hyperether.goodjob.log
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.scheduler
import com.hyperether.goodjob.scenes.components.WebFilterButton
import com.hyperether.goodjob.scenes.components.WebViewHeader
import com.hyperether.goodjob.theme.BorderColor
import com.hyperether.goodjob.theme.DashboardBg
import com.hyperether.goodjob.util.FilterDialogStateManager
import com.hyperether.goodjob.util.toScaleDp
import com.hyperether.planner.ui.ScheduleMonthPlannerWeb
import com.hyperether.planner.ui.SchedulerDayPlanner
import com.hyperether.planner.ui.WeekPlanner
import com.hyperether.resources.currentLanguage
import com.hyperether.resources.stringResource

@Composable
fun SchedulerScreenWeb(
    viewModel: SchedulerViewModel,
    navController: NavHostController,
    screenWidth: Float = 0f,
    screenHeight: Float = 0f
) {

    val periodSwitcherHeight = 56.toScaleDp()
    val webHeaderSearchAddHeight = 136.toScaleDp()
    val paddingHeight = 30.toScaleDp()

    LaunchedEffect(Unit) {
        viewModel.init()
    }
    val events = viewModel.listOfPlans.flatMap { it.events }
    log.d("SchedulerScreenWeb") { "events size: ${events.size}" }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(DashboardBg)
            .padding(bottom = 15.toScaleDp())
            .pointerInput(Unit) {
                awaitPointerEventScope {
                    while (true) {
                        val event = awaitPointerEvent()
                        val pointer = event.changes.firstOrNull()
                        if (pointer != null) {
                            viewModel.position.value = Position(
                                pointer.position.x.toDp(),
                                pointer.position.y.toDp()
                            )
                        }
                    }
                }
            }
    ) {

        WebViewHeader(
            isAddNewJob = true,
            isBackAvailable = false,
            isAddNewContact = false,
            title = stringResource(Res.string.scheduler),
            isImportAndAddVisible = false,
            navHostController = navController
        )

        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(start = 75.toScaleDp(), end = 75.toScaleDp(), top = 15.toScaleDp())
                .background(Color.White, shape = boxCornerShape())
                .border(
                    width = 1.toScaleDp(),
                    shape = boxCornerShape(),
                    color = BorderColor
                )
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 20.toScaleDp()),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {

                PeriodSwitcher(
                    text = viewModel.currentPeriod.value,
                    prev = {
                        viewModel.previous()
                        viewModel.periodChanged()
                        viewModel.parseSchedule()
                    },
                    next = {
                        viewModel.next()
                        viewModel.periodChanged()
                        viewModel.parseSchedule()
                    },
                    modifier = Modifier.width(300.toScaleDp())
                )

                Row(
                    modifier = Modifier.height(56.toScaleDp()),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Box(modifier = Modifier.weight(0.6f)) {
                        PeriodChooser(viewModel.periodState) {
                            viewModel.periodChanged()
                            viewModel.parseSchedule()
                        }
                    }
                    if (viewModel.jobList.isNotEmpty()) {
                        Spacer(Modifier.width(12.toScaleDp()))
                        WebFilterButton(
                            onClick = {
                                FilterDialogStateManager.setDialogShowing(true)
                            }
                        )
                    }
                }

            }

            Divider(
                modifier = Modifier.height(1.toScaleDp())
                    .background(Color.LightGray.copy(alpha = 0.1f))
            )

            if (viewModel.initialLoad.value) {
                viewModel.initialLoad.value = false
                viewModel.parseSchedule()
            }

            when (viewModel.periodState.value) {
                PeriodType.DAY ->
                    BoxWithConstraints(
                        modifier = Modifier.fillMaxSize()
                    ) {
                        SchedulerDayPlanner(
                            listOfPlans = viewModel.listOfPlans,
                            celWidth = 100,
                            startHour = viewModel.listOfPlans.flatMap { it.events }
                                .minOfOrNull { it.from.hour } ?: 8,
                            namesIconsShown = true,
                            onEventClicked = { event ->
                                val selectedJob =
                                    viewModel.jobList.firstOrNull { it.id == event.jobId }
                                viewModel.selectedJobDashboard.value = selectedJob
                                selectedJob?.let { viewModel.updateSite(it) }
                                viewModel.hoveredEvent.value = null
                                viewModel.jobClicked.value = true
                            },
                            screenHeight = screenHeight - periodSwitcherHeight.value - webHeaderSearchAddHeight.value - paddingHeight.value,
                            screenWidth = 0f,
                            maxWidth = maxWidth.value.toInt(),
                            isMobile = false
                        )
                    }

                PeriodType.WEEK ->
                    BoxWithConstraints(
                        modifier = Modifier.fillMaxSize()
                    ) {
                        WeekPlanner(
                            listOfPlans = viewModel.listOfPlans,
                            weekStart = viewModel.weekSelectionStart.value,
                            weekEnd = viewModel.weekSelectionEnd.value,
                            namesIconsShown = true,
                            onEventClicked = { event ->
                                val selectedJob =
                                    viewModel.jobList.firstOrNull { it.id == event.jobId }
                                viewModel.selectedJobDashboard.value = selectedJob
                                selectedJob?.let { viewModel.updateSite(it) }
                                viewModel.hoveredEvent.value = null
                                viewModel.jobClicked.value = true
                            },
                            onEventHovered = {
                                viewModel.hoveredEvent.value = it
                            },
                            maxWidth = maxWidth.value.toInt(),
                            isMobile = false
                        )
                    }

                PeriodType.MONTH -> {
                    ScheduleMonthPlannerWeb(
                        listOfPlans = viewModel.listOfPlans,
                        viewModel.monthSelectionLocalDate.value.monthNumber,
                        viewModel.monthSelectionLocalDate.value.year,
                        viewModel.monthSelectedDay,
                        language = currentLanguage.value.code,
                        onEventClicked = { event ->
                            val selectedJob = viewModel.jobList.firstOrNull { it.id == event.jobId }
                            viewModel.selectedJobDashboard.value = selectedJob
                            selectedJob?.let { viewModel.updateSite(it) }
                            viewModel.hoveredEvent.value = null
                            viewModel.jobClicked.value = true
                        },
                        onEventHovered = { event ->
                            viewModel.hoveredEvent.value = event
                        }
                    )
                }
            }
        }
    }
}

data class Position(
    val x: androidx.compose.ui.unit.Dp,
    val y: androidx.compose.ui.unit.Dp
)